import * as Sentry from "@sentry/react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import { Tabs } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import CoachingGoalsTab from "./Goals";
import CoachingMeetingsTab from "./Meetings";
import CoachingReviewsTab from "./Reviews";

import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";

import { companyAISettingsQuery, currentUserQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";

import ErrorPage from "../../Error";

export const coachingSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const CoachingSettings = () => {
	const [searchParams] = useSearchParams();

	// feature flags
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: coachingEnabled } = useFeatureFlagQuery("ff-is-coaching-enabled", false);
	const { value: meetingsEnabled } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	// Unsaved changes tracking is held at the page level so it survives tab content swaps and
	// blocks both route changes and tab (search param) changes while a tab's form is dirty.
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	const tabs = [
		...(meetingsEnabled
			? [
					{
						value: "meetings",
						label: "Meetings",
						content: <CoachingMeetingsTab setIsUnsafe={setIsUnsafe} setIsSafe={setIsSafe} />,
					},
				]
			: []),
		{
			value: "goals",
			label: "Goals",
			content: <CoachingGoalsTab setIsUnsafe={setIsUnsafe} setIsSafe={setIsSafe} />,
		},
		{
			value: "reviews",
			label: "Reviews",
			content: <CoachingReviewsTab setIsUnsafe={setIsUnsafe} setIsSafe={setIsSafe} />,
		},
	];

	// The page is gated by the AI + Coaching feature flags.
	if (!aiFeatureFlagEnabled || !coachingEnabled) {
		return <ErrorPage code={404} />;
	}

	// Set the active tab based on the URL parameter; fall back to the first valid tab.
	const validTabValues = tabs.map((tab) => tab.value);
	const tabFromUrl = searchParams.get("tab");
	const activeTab = tabFromUrl && validTabValues.includes(tabFromUrl) ? tabFromUrl : tabs[0].value;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="Coaching" />
				<AdminTitle>Coaching</AdminTitle>
				<Spacer height={3} />
				<Tabs
					tabs={tabs}
					value={activeTab}
					urlParam="tab"
					color="neutral"
					size="large"
					dangerouslySetClassName="gap-10"
				/>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default CoachingSettings;
